/**
 * Telegram Mini Apps Utilities
 * Production-ready, SSR-safe, no crashes outside Telegram WebView
 */

import { getTelegram, isTelegramAvailable, type TelegramWebAppLike } from './telegramAdapter';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

type TelegramWebApp = TelegramWebAppLike;

function getWebApp(): TelegramWebApp | undefined {
  if (typeof window === 'undefined') return undefined;
  return isTelegramAvailable() ? window.Telegram?.WebApp : undefined;
}

export function isTelegramWebApp(): boolean {
  return isTelegramAvailable();
}

/**
 * Enhanced Telegram detection with user context validation.
 * Returns true only if running inside Telegram AND has valid user data.
 */
export function isValidTelegramContext(): boolean {
  const webApp = getWebApp();
  if (!webApp) return false;
  
  // Check if we have valid user data from initDataUnsafe
  const user = webApp.initDataUnsafe?.user;
  return !!(user && user.id);
}

let viewportVarsBound = false;

function syncTelegramViewportVars(webApp: TelegramWebApp): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--tg-viewport-height', `${webApp.viewportHeight}px`);
  root.style.setProperty('--tg-viewport-stable-height', `${webApp.viewportStableHeight}px`);
}

export function initTelegramWebApp(): void {
  const webApp = getWebApp();
  if (!webApp) {
    console.warn('[Telegram] Not running in Telegram WebApp - fallback mode');
    return;
  }

  try {
    webApp.ready?.();

    syncTelegramViewportVars(webApp);
    if (!viewportVarsBound && typeof window !== 'undefined') {
      viewportVarsBound = true;
      window.addEventListener('resize', () => {
        const wa = getWebApp();
        if (wa) syncTelegramViewportVars(wa);
      });
    }

    setTimeout(() => {
      try {
        webApp.expand?.();
        syncTelegramViewportVars(webApp);
        console.log('[Telegram] WebApp initialized and expanded');
      } catch (err) {
        console.warn('[Telegram] Expand failed:', err);
      }
    }, 100);
  } catch (error) {
    console.error('[Telegram] Initialization failed:', error);
  }
}

export function getTelegramUser(): TelegramUser | null {
  return getTelegram().initDataUnsafe?.user ?? null;
}

export function openExternal(url: string): void {
  const webApp = getWebApp();
  if (webApp) {
    try {
      if (webApp.openLink) {
        webApp.openLink(url);
      } else if (url.startsWith('https://t.me/') && webApp.openTelegramLink) {
        webApp.openTelegramLink(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.warn('[Telegram] openExternal failed, fallback to window.open:', err);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Автоматически отправляет сгенерированное фото в чат с ботом через backend API.
 * Убирает необходимость в Web Share API и download кнопках.
 * 
 * @param generatedImageId - ID сгенерированного изображения
 * @returns Promise<'success' | 'error'>
 */
export async function sendPhotoToTelegramChat(generatedImageId: string): Promise<'success' | 'error' | 'forbidden' | 'no_auth'> {
  try {
    console.log('[SendToTelegram] Starting auto-send to bot chat, imageId:', generatedImageId);
    
    // Проверяем наличие Telegram initData
    const { getTelegramAuthHeaders, hasTelegramAuth } = await import('./telegramAuth');
    
    if (!hasTelegramAuth()) {
      console.error('[SendToTelegram] No Telegram initData available');
      return 'no_auth';
    }
    
    // Получаем заголовки авторизации
    const authHeaders = getTelegramAuthHeaders();
    
    // Отправляем запрос на backend с обязательной авторизацией
    const response = await fetch(`/api/generated/${generatedImageId}/send-to-telegram`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[SendToTelegram] Backend error:', response.status, errorText);
      
      // Возвращаем специальный статус для 403 (нет подписки)
      if (response.status === 403) {
        return 'forbidden';
      }
      
      return 'error';
    }

    const result = await response.json();
    console.log('[SendToTelegram] Success:', result);
    
    // Показываем haptic feedback при успехе
    if (isTelegramWebApp()) {
      notificationFeedback('success');
    }
    
    return 'success';
  } catch (err) {
    console.error('[SendToTelegram] Error:', err);
    return 'error';
  }
}

export function showMainButton(text: string, onClick: () => void): void {
  const webApp = getWebApp();
  if (!webApp) return;
  const mainButton = webApp.MainButton;
  if (!mainButton) return;
  mainButton.text = text;
  mainButton.show();
  mainButton.enable();
  mainButton.onClick(onClick);
}

export function hideMainButton(): void {
  const webApp = getWebApp();
  if (!webApp) return;
  webApp.MainButton?.hide();
}

export function hapticFeedback(type: 'light' | 'medium' | 'heavy' = 'medium'): void {
  const webApp = getWebApp();
  if (!webApp) return;
  webApp.HapticFeedback?.impactOccurred(type);
}

export function notificationFeedback(type: 'error' | 'success' | 'warning'): void {
  const webApp = getWebApp();
  if (!webApp) return;
  webApp.HapticFeedback?.notificationOccurred(type);
}

export function closeTelegramWebApp(): void {
  const webApp = getWebApp();
  if (!webApp) return;
  webApp.close?.();
}


